/**
 * RecruitSheet - a study's recruitment list, /crm/recruit/:slug (docs/plans/crm-recruitment-list-plan.md
 * Part A sections 2 to 6, B4, B5, B7).
 *
 * Every lead still open on the study, needs-first, with a plain-words reason on every row, in the
 * spreadsheet the recruiters keep today (Sheet) or as cards (Cards, and always on a phone). The
 * header holds the owner picker (Everyone on the study by default, decision 3), the status chips
 * with exact counts, the two attention-tier chips (Needs action now, Keep trying: backend
 * services/crmAttention.js, owner 2026-09-28), Show closed, Show archived, the search box, Work
 * through and the column chooser; the bulk bar shows when rows are ticked.
 *
 * Loading (B2, B7): the slug is resolved to the study through the picker's own list (recruiting
 * and paused studies; anything else has no recruitment list), then the rows are read 200 at a time
 * until a short page, the server's `capped`, or the 2,000-row ceiling, and sorted needs-first. The
 * list refreshes itself every minute while the tab is visible, soon after an SMS-feed poke for a
 * lead on it (OUTREACH_REPLY_EVENT, the WorkQueueContext pattern) and shortly after every write
 * made from it; those refreshes merge (recruitLogic mergeRefetch): nothing moves under the
 * recruiter, an open editor keeps its draft, and a row closed from the sheet stays where it is,
 * greyed, until a manual refresh or a filter change (decision 6).
 *
 * Writes never go through this page: the cells and the bulk bar use the lead page's own endpoints
 * (the per-study status PATCH, the owner PATCH) and the recruit note and attempt routes. Work
 * through hands the rows on screen, in the sheet's order, to the Next-up bar (plan B5): the
 * needs-action-now rows first, and when those are done the bar offers the keep-trying ones; with
 * the Keep trying chip on it serves those.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ClipboardCheck, LayoutGrid, Loader2, Play, SearchX, Table as TableIcon, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader, { RefreshButton } from '../components/PageHeader';
import { FilterBar, FilterChip, FilterMultiSelect, SearchInput, SegmentedControl } from '../components/filters';
import { Alert, useConfirm } from '../components/ui';
import { MEMBERSHIP_LIFECYCLES, getPref, setPref, useIsPhone, useTeam } from '../lib';
import { studyInternalLabel } from '../types';
import { useWorkQueue } from '../WorkQueueContext';
import {
  OUTREACH_REPLY_EVENT,
  UNASSIGNED,
  ownersParam,
  type OwnerSelection,
  type WorkQueueStudyFilters,
} from '../workQueueLogic';
import {
  getRecruitColumns,
  isRecruitUnavailable,
  listRecruitStudies,
  loadAllRecruitRows,
  recruitErrorMessage,
} from './api';
import ColumnPicker, { readColumnPrefs, writeColumnPrefs } from './ColumnPicker';
import RecruitCards from './RecruitCards';
import {
  RECRUIT_ROW_CAP,
  capMessage,
  compareRows,
  isTerminalStatus,
  keepTrying,
  mergeRefetch,
  needsAction,
  rowsToQueueItems,
  studyQueueRef,
  visibleColumns,
  type EditingCell,
} from './recruitLogic';
import BulkBar from './sheet/BulkBar';
import SheetGrid from './sheet/SheetGrid';
import type {
  RecruitAnswerColumn,
  RecruitCounts,
  RecruitRow,
  RecruitRowChangeHandler,
  RecruitRowsParams,
  RecruitScope,
  RecruitSelectHandler,
  RecruitSort,
  RecruitStudy,
  RecruitStudyCard,
  RecruitView,
  SheetColumnPrefs,
} from './types';

/** The list refreshes itself this often while the tab is visible (B7). */
const POLL_MS = 60_000;
/** A write from the sheet is followed by a refresh this soon (writes in a burst share one). */
const REFRESH_AFTER_WRITE_MS = 1_200;
/** An SMS-feed poke for a lead on the list refreshes it this soon. */
const REFRESH_AFTER_REPLY_MS = 400;
/** Coming back to the tab after this long refreshes at once instead of waiting for the poll. */
const STALE_AFTER_HIDDEN_MS = 30_000;
/** Mirrors backend crmWorklist DEFAULT_MAX_ATTEMPTS / DEFAULT_STALE_DAYS, until the rows arrive. */
const FALLBACK_MAX_ATTEMPTS = 6;
const FALLBACK_STALE_DAYS = 4;

/** Pref keys under lib's `crm.` prefix (plan B1). */
const VIEW_PREF = 'recruit.view';
const OWNERS_PREF = 'recruit.owners';
const SCOPE_PREF = 'recruit.scope';
const sortPrefKey = (studyId: string): string => `recruit.sort.${studyId}`;
/** A stored '' is a real choice (Everyone); "never chosen" needs a value the pref never holds. */
const NEVER_CHOSEN = '__unset__';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The owner picker's remembered choice. Never chosen, or chosen Everyone, is [] = everyone on the
 * study (decision 3). Anything unreadable is dropped rather than sent: the server would fall back
 * to "my leads + unassigned" for junk, which is not what the recruiter picked.
 */
function readOwnersPref(): OwnerSelection {
  const raw = getPref(OWNERS_PREF, NEVER_CHOSEN);
  if (raw === NEVER_CHOSEN || raw === '') return [];
  return raw.split(',').filter((t) => t === UNASSIGNED || UUID_RE.test(t));
}

function readSortPref(studyId: string): RecruitSort | null {
  const raw = getPref(sortPrefKey(studyId), '');
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    const s = parsed as Partial<RecruitSort> | null;
    if (!s || typeof s !== 'object' || typeof s.key !== 'string' || !s.key) return null;
    if (s.dir !== 'asc' && s.dir !== 'desc') return null;
    return { key: s.key as RecruitSort['key'], dir: s.dir };
  } catch {
    return null;
  }
}

function writeSortPref(studyId: string, sort: RecruitSort | null): void {
  setPref(sortPrefKey(studyId), sort ? JSON.stringify(sort) : '');
}

/** The rows route's query for the page's filters. Status chips win over the open scope. */
function rowsQuery(f: {
  owners: OwnerSelection;
  meId: string | null;
  scope: RecruitScope;
  statuses: string[];
  q: string;
  showArchived: boolean;
}): Omit<RecruitRowsParams, 'limit' | 'offset'> {
  const params: Omit<RecruitRowsParams, 'limit' | 'offset'> = {
    // Always explicit: 'all' is everyone on the study, never the server's own default.
    owners: ownersParam(f.owners, f.meId),
    scope: f.scope,
  };
  if (f.statuses.length) params.status = f.statuses.join(',');
  if (f.q) params.q = f.q;
  if (f.showArchived) params.show_archived = true;
  return params;
}

/** The same study as before (a refresh sends a new, equal object: keep the old one, keep the rows still). */
function sameStudy(a: RecruitStudy | null, b: RecruitStudy): boolean {
  return !!a && a.id === b.id && a.slug === b.slug && a.title === b.title
    && a.internal_title === b.internal_title && a.status === b.status
    && a.max_attempts === b.max_attempts && a.stale_days === b.stale_days;
}

/** The study's answer columns from the rows themselves (when the columns call failed). */
function answerColumnsFromRows(rows: readonly RecruitRow[]): RecruitAnswerColumn[] {
  const out: RecruitAnswerColumn[] = [];
  const seen = new Set<string>();
  for (const row of rows.slice(0, 200)) {
    const answers = row.answers || {};
    for (const key of Object.keys(answers)) {
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ question_key: key, label: answers[key]?.label || key });
    }
  }
  return out;
}

const primaryBtnCls =
  'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0';

/** /crm/recruit/:slug. One page instance per study: moving to another study starts clean. */
export default function RecruitSheet() {
  const { slug = '' } = useParams<{ slug: string }>();
  return <RecruitSheetPage key={slug} slug={slug} />;
}

function RecruitSheetPage({ slug }: { slug: string }) {
  const location = useLocation();
  const isPhone = useIsPhone();
  const { user } = useAuth();
  const meId = user?.id ?? null;
  const { team, byId } = useTeam();
  const queue = useWorkQueue();
  const confirm = useConfirm();

  // --- The study (the slug through the picker's list) ------------------------------------------
  const [card, setCard] = useState<RecruitStudyCard | null>(null);
  const [resolved, setResolved] = useState<'loading' | 'ready' | 'missing' | 'unavailable' | 'error'>('loading');
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolveTry, setResolveTry] = useState(0);

  useEffect(() => {
    let alive = true;
    setResolved('loading');
    listRecruitStudies()
      .then((studies) => {
        if (!alive) return;
        const hit = studies.find((s) => s.slug === slug) || null;
        setCard(hit);
        setResolved(hit ? 'ready' : 'missing');
      })
      .catch((e: unknown) => {
        if (!alive) return;
        if (isRecruitUnavailable(e)) {
          setResolved('unavailable');
          return;
        }
        setResolveError(recruitErrorMessage(e, "This study's list couldn't be loaded. Please try again."));
        setResolved('error');
      });
    return () => {
      alive = false;
    };
  }, [slug, resolveTry]);

  const studyId = card?.id ?? null;

  // --- Filters and per-device choices (plan B1) -------------------------------------------------
  const [view, setView] = useState<RecruitView>(() => (getPref(VIEW_PREF, 'sheet') === 'cards' ? 'cards' : 'sheet'));
  const [owners, setOwners] = useState<OwnerSelection>(readOwnersPref);
  const [showClosed, setShowClosed] = useState<boolean>(() => getPref(SCOPE_PREF, 'open') === 'all');
  const [statuses, setStatuses] = useState<string[]>([]);
  // The attention-tier filter: 'now' keeps the needs-action-now rows, 'later' the keep-trying ones.
  const [tierFilter, setTierFilter] = useState<'now' | 'later' | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<RecruitSort | null>(null);
  const [colPrefs, setColPrefs] = useState<SheetColumnPrefs | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setQ(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!studyId) return;
    setSort(readSortPref(studyId));
    setColPrefs(readColumnPrefs(studyId));
  }, [studyId]);

  // --- The rows ---------------------------------------------------------------------------------
  const [rows, setRows] = useState<RecruitRow[]>([]);
  const [studyInfo, setStudyInfo] = useState<RecruitStudy | null>(null);
  const [counts, setCounts] = useState<RecruitCounts | null>(null);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [notice, setNotice] = useState<string | null>(null);
  const [answerColumns, setAnswerColumns] = useState<RecruitAnswerColumn[]>([]);
  const [columnsFailed, setColumnsFailed] = useState(false);

  const nameOf = useCallback((id: string) => byId.get(id)?.name ?? null, [byId]);
  const comparator = useMemo(() => compareRows(sort, { nameOf }), [sort, nameOf]);

  const scope: RecruitScope = statuses.length > 0 || showClosed ? 'all' : 'open';
  const query = useMemo(
    () => rowsQuery({ owners, meId, scope, statuses, q, showArchived }),
    [owners, meId, scope, statuses, q, showArchived],
  );
  const queryKey = JSON.stringify(query);

  // What the async loaders read: always the latest, without re-arming the poll.
  const studyIdRef = useRef(studyId);
  studyIdRef.current = studyId;
  const queryRef = useRef(query);
  queryRef.current = query;
  const comparatorRef = useRef(comparator);
  comparatorRef.current = comparator;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const genRef = useRef(0); // a fresh load supersedes everything in flight (the ByStudy counter)
  const freshInFlightRef = useRef(false);
  const mergingRef = useRef(false);
  const mergeAgainRef = useRef(false);
  const lastLoadRef = useRef(0);
  // Rows closed from the sheet: kept, greyed, through the minute's refresh (decision 6).
  const closedRef = useRef<Set<string>>(new Set());
  // The cell whose editor is open (SheetGrid reports it): a refresh keeps its draft.
  const editingRef = useRef<EditingCell | null>(null);

  /** Read the whole list afresh and sort it: first load, a filter change, the Refresh button. */
  const loadFresh = useCallback(async () => {
    const id = studyIdRef.current;
    if (!id) return;
    const gen = genRef.current + 1;
    genRef.current = gen;
    freshInFlightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await loadAllRecruitRows(id, queryRef.current);
      if (gen !== genRef.current) return;
      closedRef.current = new Set();
      setRows([...res.rows].sort(comparatorRef.current));
      setCounts(res.counts);
      setCapped(res.capped);
      if (res.study && res.study.id) {
        const next = res.study;
        setStudyInfo((prev) => (sameStudy(prev, next) ? prev : next));
      }
      setLoaded(true);
      lastLoadRef.current = Date.now();
    } catch (e: unknown) {
      if (gen !== genRef.current) return;
      setError(recruitErrorMessage(e, "The list couldn't be loaded. Please try again."));
    } finally {
      if (gen === genRef.current) {
        freshInFlightRef.current = false;
        setLoading(false);
      }
    }
  }, []);

  /** The quiet refresh: fold the server's rows into the list without moving anything. */
  const loadMerge = useCallback(async () => {
    const id = studyIdRef.current;
    if (!id || freshInFlightRef.current) return;
    if (mergingRef.current) {
      mergeAgainRef.current = true;
      return;
    }
    mergingRef.current = true;
    const gen = genRef.current;
    try {
      const res = await loadAllRecruitRows(id, queryRef.current);
      if (gen !== genRef.current) return;
      setRows((prev) => mergeRefetch(prev, res.rows, editingRef.current, {
        keep: closedRef.current,
        compare: comparatorRef.current,
      }));
      setCounts(res.counts);
      setCapped(res.capped);
      if (res.study && res.study.id) {
        const next = res.study;
        setStudyInfo((prev) => (sameStudy(prev, next) ? prev : next));
      }
      lastLoadRef.current = Date.now();
    } catch {
      /* a missed refresh is harmless: the next one, or the minute's poll, catches up */
    } finally {
      mergingRef.current = false;
      if (mergeAgainRef.current) {
        mergeAgainRef.current = false;
        void loadMergeRef.current();
      }
    }
  }, []);
  const loadMergeRef = useRef(loadMerge);
  loadMergeRef.current = loadMerge;

  const refreshTimerRef = useRef<number | null>(null);
  const scheduleRefresh = useCallback((delay: number = REFRESH_AFTER_WRITE_MS) => {
    if (refreshTimerRef.current !== null) window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      void loadMergeRef.current();
    }, delay);
  }, []);
  useEffect(() => () => {
    if (refreshTimerRef.current !== null) window.clearTimeout(refreshTimerRef.current);
  }, []);

  // A filter change (or the study arriving) reads the list afresh; the selection clears with it.
  useEffect(() => {
    if (!studyId || !queryKey) return;
    setSelection((prev) => (prev.size ? new Set<string>() : prev));
    void loadFresh();
  }, [studyId, queryKey, loadFresh]);

  // The study's answer columns (decision 7). Best effort: without them the sheet still works.
  useEffect(() => {
    if (!studyId) return undefined;
    let alive = true;
    getRecruitColumns(studyId)
      .then((res) => {
        if (alive) setAnswerColumns(res.columns);
      })
      .catch(() => {
        if (alive) setColumnsFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [studyId]);

  // The minute's refresh while the tab is visible, a quick one on coming back to a stale tab,
  // and an early one when the SMS feed says a lead on this list just texted.
  useEffect(() => {
    if (!studyId) return undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') void loadMergeRef.current();
    };
    const timer = window.setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastLoadRef.current > STALE_AFTER_HIDDEN_MS) {
        void loadMergeRef.current();
      }
    };
    const onReply = (e: Event) => {
      const contactId = (e as CustomEvent<{ contactId?: string } | null>).detail?.contactId;
      if (contactId && rowsRef.current.some((r) => r.contact.id === contactId)) scheduleRefresh(REFRESH_AFTER_REPLY_MS);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(OUTREACH_REPLY_EVENT, onReply);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(OUTREACH_REPLY_EVENT, onReply);
    };
  }, [studyId, scheduleRefresh]);

  // --- Rows on screen ----------------------------------------------------------------------------
  const displayRows = useMemo(
    () => (tierFilter
      // "Needs action now" / "Keep trying" keep that tier's rows; a row just closed here stays put.
      ? rows.filter((r) => (tierFilter === 'now' ? needsAction(r.reason) : keepTrying(r.reason))
        || closedRef.current.has(r.contact.id))
      : rows),
    [rows, tierFilter],
  );
  // The two chips' numbers: open rows in each tier, over everything loaded (never the filtered view).
  const tierCounts = useMemo(() => {
    let now = 0;
    let later = 0;
    for (const r of rows) {
      if (isTerminalStatus(r.membership.status)) continue;
      if (needsAction(r.reason)) now += 1;
      else if (keepTrying(r.reason)) later += 1;
    }
    return { now, later };
  }, [rows]);
  const displayRowsRef = useRef(displayRows);
  displayRowsRef.current = displayRows;

  // A refresh that drops a ticked row clears the selection: a bulk edit never acts on rows the
  // recruiter can no longer see.
  useEffect(() => {
    if (selection.size === 0) return;
    const present = new Set(displayRows.map((r) => r.contact.id));
    if (Array.from(selection).some((id) => !present.has(id))) setSelection(new Set<string>());
  }, [displayRows, selection]);

  const selectedRows = useMemo(
    () => (selection.size ? displayRows.filter((r) => selection.has(r.contact.id)) : []),
    [displayRows, selection],
  );

  const study: RecruitStudy | null = useMemo(() => {
    if (studyInfo) return studyInfo;
    if (!card) return null;
    return {
      id: card.id,
      slug: card.slug,
      title: card.title,
      internal_title: card.internal_title,
      status: card.status,
      max_attempts: FALLBACK_MAX_ATTEMPTS,
      stale_days: FALLBACK_STALE_DAYS,
    };
  }, [studyInfo, card]);

  const effectiveAnswerColumns = useMemo(
    () => (columnsFailed && answerColumns.length === 0 ? answerColumnsFromRows(rows) : answerColumns),
    [columnsFailed, answerColumns, rows],
  );
  const columns = useMemo(() => visibleColumns(colPrefs, effectiveAnswerColumns), [colPrefs, effectiveAnswerColumns]);

  // --- Handlers ----------------------------------------------------------------------------------
  const handleRowChange = useCallback<RecruitRowChangeHandler>((contactId, update) => {
    const before = rowsRef.current.find((r) => r.contact.id === contactId);
    if (before) {
      const after = update(before);
      if (isTerminalStatus(after.membership.status)) closedRef.current.add(contactId);
      else closedRef.current.delete(contactId);
    }
    setRows((prev) => prev.map((r) => (r.contact.id === contactId ? update(r) : r)));
    scheduleRefresh();
  }, [scheduleRefresh]);

  const handleSelect = useCallback<RecruitSelectHandler>((contactId, on) => {
    setSelection((prev) => {
      if (prev.has(contactId) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(contactId);
      else next.delete(contactId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((on: boolean) => {
    setSelection(on ? new Set(displayRowsRef.current.map((r) => r.contact.id)) : new Set<string>());
  }, []);

  const clearSelection = useCallback(() => setSelection(new Set<string>()), []);

  const onEditingChange = useCallback((cell: EditingCell | null) => {
    editingRef.current = cell;
  }, []);

  const searchWrapRef = useRef<HTMLDivElement>(null);
  const focusSearch = useCallback(() => {
    searchWrapRef.current?.querySelector('input')?.focus();
  }, []);

  const chooseView = (value: string) => {
    const next: RecruitView = value === 'cards' ? 'cards' : 'sheet';
    setView(next);
    setPref(VIEW_PREF, next);
  };

  const chooseOwners = (next: OwnerSelection) => {
    setOwners(next);
    setPref(OWNERS_PREF, next.join(','));
  };

  const toggleShowClosed = () => {
    const next = !showClosed;
    setShowClosed(next);
    setPref(SCOPE_PREF, next ? 'all' : 'open');
  };

  const toggleStatus = (value: string) => {
    setStatuses((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
  };

  const toggleTier = (tier: 'now' | 'later') => {
    setTierFilter((v) => (v === tier ? null : tier));
    setSelection(new Set<string>());
  };

  const changeSort = (next: RecruitSort | null) => {
    setSort(next);
    if (studyId) writeSortPref(studyId, next);
    const cmp = compareRows(next, { nameOf });
    setRows((prev) => [...prev].sort(cmp));
  };

  const changeColumns = (next: SheetColumnPrefs | null) => {
    setColPrefs(next);
    if (studyId) writeColumnPrefs(studyId, next);
  };

  // --- Work through (plan B5; the attention tiers, owner 2026-09-28) -----------------------------
  // Urgent first, then offer the rest: the needs-action-now rows on screen, in the sheet's order;
  // when they are done the Next-up bar offers the keep-trying ones. With the Keep trying chip on it
  // serves those. Rows with nothing due are never served.
  const workThrough = async () => {
    if (!study) return;
    setNotice(null);
    const rowsNow = displayRowsRef.current;
    let tier: 'now' | 'later' = tierFilter === 'later' ? 'later' : 'now';
    let items = rowsToQueueItems(rowsNow, study, { tier });
    const later = tier === 'now' ? rowsToQueueItems(rowsNow, study, { tier: 'later' }).length : 0;
    if (items.length === 0 && later > 0) {
      // Nothing needs action now: offer the keep-trying work rather than starting it unasked.
      const ok = await confirm({
        title: 'Nothing here needs action right now',
        message: `${later.toLocaleString()} ${later === 1 ? 'lead on this list is' : 'leads on this list are'} due another try but ${later === 1 ? 'is' : 'are'}n't waiting on us. Work through ${later === 1 ? 'that one' : 'them'}?`,
        confirmLabel: 'Keep trying',
        cancelLabel: 'Not now',
      });
      if (!ok) return;
      tier = 'later';
      items = rowsToQueueItems(rowsNow, study, { tier });
    }
    if (items.length === 0) {
      setNotice('Nothing on this list is due right now.');
      return;
    }
    const filters: WorkQueueStudyFilters = {
      owners,
      scope,
      statuses: statuses.length ? statuses : null,
      q: q || null,
      showArchived,
      onlyNeedsAction: tierFilter === 'now',
      includeWaiting: false,
      tier,
    };
    const started = await queue.startRows(items, studyQueueRef(study), {
      returnTo: `${location.pathname}${location.search}`,
      filters,
      keepTrying: tier === 'now' ? later : null,
    });
    if (!started) setNotice('Nothing on this list could be started. Refresh the list and try again.');
  };

  // --- Render ------------------------------------------------------------------------------------
  const ownerOptions = useMemo(() => [
    { value: '', label: 'Everyone on the study' },
    ...(meId ? [{ value: meId, label: 'My leads' }] : []),
    { value: UNASSIGNED, label: 'Unassigned' },
    ...team.filter((m) => m.id !== meId).map((m) => ({ value: m.id, label: m.name })),
  ], [team, meId]);

  if (resolved === 'loading') {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-label="Loading" />
      </div>
    );
  }

  if (resolved !== 'ready' || !card || !study) {
    const unavailable = resolved === 'unavailable';
    const failed = resolved === 'error';
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <SearchX className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" aria-hidden="true" />
        <h1 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">
          {unavailable
            ? "The recruitment list isn't available yet"
            : failed
              ? "This recruitment list couldn't be loaded"
              : 'This study has no recruitment list: it is not recruiting'}
        </h1>
        <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
          {unavailable
            ? 'It is on its way. Try again in a little while.'
            : failed
              ? resolveError
              : 'Only studies that are recruiting, or paused, have one. Pick a study from Recruit.'}
        </p>
        <div className="mt-4 flex items-center gap-2">
          {failed && (
            <button type="button" onClick={() => setResolveTry((n) => n + 1)} className={primaryBtnCls}>
              Try again
            </button>
          )}
          <Link
            to="/crm/recruit"
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            All studies
          </Link>
        </div>
      </div>
    );
  }

  const effectiveView: RecruitView = isPhone ? 'cards' : view;
  const studyName = studyInternalLabel(study) || study.slug;
  const subtitle = [
    study.internal_title && study.title && study.internal_title !== study.title ? study.title : null,
    study.status === 'paused' ? 'Paused' : null,
  ].filter(Boolean).join(' · ');
  const capText = capped ? capMessage(Math.max(rows.length, RECRUIT_ROW_CAP)) : null;
  const filtered = statuses.length > 0 || !!q || tierFilter !== null || owners.length > 0 || showArchived;
  const chips = MEMBERSHIP_LIFECYCLES.filter(
    (l) => (counts?.by_status?.[l.value] ?? 0) > 0 || statuses.includes(l.value),
  );
  return (
    <div className="space-y-3">
      <PageHeader
        icon={ClipboardCheck}
        title={`${studyName} recruitment list`}
        count={loaded ? displayRows.length : null}
        subtitle={subtitle || undefined}
        actions={(
          <>
            {!isPhone && (
              <SegmentedControl
                label="View"
                value={view}
                onChange={chooseView}
                options={[
                  { value: 'sheet', label: 'Sheet', icon: <TableIcon className="h-4 w-4" aria-hidden="true" />, title: 'Sheet: the spreadsheet' },
                  { value: 'cards', label: 'Cards', icon: <LayoutGrid className="h-4 w-4" aria-hidden="true" />, title: 'Cards: one card per lead' },
                ]}
              />
            )}
            <RefreshButton onClick={() => { void loadFresh(); }} loading={loading} label="Refresh the list" />
          </>
        )}
      />

      <FilterBar>
        <FilterMultiSelect
          auto
          values={owners}
          onChange={chooseOwners}
          options={ownerOptions}
          label="Whose leads"
          placeholder="Everyone"
          title="Whose leads the list shows"
        />
        <div ref={searchWrapRef} className="min-w-0 flex-1 basis-56 sm:max-w-xs">
          <SearchInput value={search} onChange={setSearch} placeholder="Name, phone or email" label="Search the list" />
        </div>
        <FilterChip
          active={tierFilter === 'now'}
          onClick={() => toggleTier('now')}
          title="Only the leads who need you now: a reply owed, a call due, a hot new lead, a conversation gone quiet"
        >
          Needs action now{loaded ? ` (${tierCounts.now.toLocaleString()})` : ''}
        </FilterChip>
        <FilterChip
          active={tierFilter === 'later'}
          onClick={() => toggleTier('later')}
          title="Only the leads due another try who are not waiting on us: no answer yet, gone quiet, never contacted"
        >
          Keep trying{loaded ? ` (${tierCounts.later.toLocaleString()})` : ''}
        </FilterChip>
        <FilterChip active={showClosed} onClick={toggleShowClosed} title="Also list the leads closed on this study">
          Show closed
        </FilterChip>
        <FilterChip active={showArchived} onClick={() => setShowArchived((v) => !v)} title="Also list archived leads">
          Show archived
        </FilterChip>
        {effectiveView === 'sheet' && (
          <ColumnPicker prefs={colPrefs} answerColumns={effectiveAnswerColumns} onChange={changeColumns} />
        )}
        <button
          type="button"
          onClick={() => { void workThrough(); }}
          disabled={queue.starting || !loaded || displayRows.length === 0}
          title="Work through the leads on screen, in this order, in the Next-up bar"
          className={`${primaryBtnCls} sm:ml-auto`}
        >
          {queue.starting
            ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            : <Play className="h-4 w-4" aria-hidden="true" />}
          Work through
        </button>
      </FilterBar>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Status on this study">
          {chips.map((l) => {
            const on = statuses.includes(l.value);
            const dimmed = statuses.length > 0 && !on;
            return (
              <button
                key={l.value}
                type="button"
                onClick={() => toggleStatus(l.value)}
                aria-pressed={on}
                title={on ? `Showing ${l.label}: click to show all` : `Show only ${l.label}`}
                className={`inline-flex min-h-[44px] items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:min-h-0 ${l.badge} ${
                  on ? 'ring-2 ring-primary-500 ring-offset-1 dark:ring-offset-gray-900' : ''
                } ${dimmed ? 'opacity-50 hover:opacity-100' : ''}`}
              >
                {l.label}
                <span className="font-semibold tabular-nums">{(counts?.by_status?.[l.value] ?? 0).toLocaleString()}</span>
              </button>
            );
          })}
          {statuses.length > 0 && (
            <button
              type="button"
              onClick={() => setStatuses([])}
              className="inline-flex min-h-[44px] items-center gap-1 rounded-full border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
            >
              <X className="h-3 w-3" aria-hidden="true" /> Show all
            </button>
          )}
        </div>
      )}

      {capText && <Alert tone="warning">{capText}</Alert>}
      {error && (
        <Alert
          tone="error"
          icon={false}
          actions={(
            <button
              type="button"
              onClick={() => { void loadFresh(); }}
              className="inline-flex min-h-[44px] items-center rounded-lg border border-red-300 px-3 py-1 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 sm:min-h-0"
            >
              Retry
            </button>
          )}
        >
          {error}
        </Alert>
      )}
      {notice && <Alert tone="info" onDismiss={() => setNotice(null)}>{notice}</Alert>}

      {/* In Cards the cards carry their own bulk bar (RecruitCards); one bar per view. */}
      {effectiveView === 'sheet' && (
        <BulkBar selected={selectedRows} study={study} onRowChange={handleRowChange} onClear={clearSelection} />
      )}

      {!loaded ? (
        <div className="flex items-center justify-center py-16">
          {loading && <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-label="Loading" />}
        </div>
      ) : displayRows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ClipboardCheck className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" aria-hidden="true" />
          <h2 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">
            {filtered ? 'No leads match' : 'No open leads on this study'}
          </h2>
          <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
            {filtered
              ? 'Try clearing or broadening the filters.'
              : 'New leads for this study appear here as they come in.'}
          </p>
        </div>
      ) : effectiveView === 'sheet' ? (
        <SheetGrid
          rows={displayRows}
          study={study}
          columns={columns}
          sort={sort}
          onSortChange={changeSort}
          selection={selection}
          onSelect={handleSelect}
          onSelectAll={handleSelectAll}
          onRowChange={handleRowChange}
          onEditingChange={onEditingChange}
          onFocusSearch={focusSearch}
          label={`${studyName} recruitment list`}
        />
      ) : (
        <RecruitCards
          rows={displayRows}
          study={study}
          selection={selection}
          onRowChange={handleRowChange}
          onSelect={handleSelect}
        />
      )}
    </div>
  );
}
